import HeaderWordmark from '../shared/HeaderWordmark';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useUiV2 } from '../../config/uiVersion';
import useAdminConsole from '../../hooks/useAdminConsole';
import useConfirmGuard from '../../hooks/useConfirmGuard';
import {
    COPY, findNode, PERSPECTIVE_LABEL, ROLE_LABEL, tileFoot, TYPE_LABEL, initials,
} from '../../utils/adminConsole';
import AddUserModal from './AddUserModal';
import OnboardWizard from './OnboardWizard';
import OrgEditModal from './OrgEditModal';
import TenantOverview from './TenantOverview';
import AdminConsoleV2 from '../AdminV2/AdminConsoleV2';
import './AdminConsole.css';

// Today's console (the new design flag off). State and calls live in
// hooks/useAdminConsole, shared with the redesign (components/AdminV2).
function AdminConsoleV1({ me, onSwitchToApp, onLogout }) {
    const {
        orgs, loading, usersLoading, error, actionError,
        currentNode, childOrgs, crumbs, kpis, childLabel, teamLabel, childAdd, drill, goTo,
        isSuper, canEdit, roleChoices, orgOptions, query, setQuery, filteredUsers,
        busyId, patchUser, confirmId, setConfirmId, doRemove, resetConfirm, setResetConfirm, doResetPw,
        resetResult, setResetResult, editTarget, setEditTarget, deleteTarget, setDeleteTarget, openDelete, doDelete,
        addChild, setAddChild, openAddChild, addChildName, setAddChildName, doAddChild, orgBusy, orgError,
        showAdd, setShowAdd, showOnboard, setShowOnboard, onUserAdded, onOnboarded, onOrgSaved,
    } = useAdminConsole({ me });
    // 'orgs' = the org tree + team table, 'sources' = the data-source overview.
    const [view, setView] = useState('orgs');
    // The shared 400 ms / double-click guard on the delete confirm (QA A-1).
    const deleteGuard = useConfirmGuard(Boolean(deleteTarget));

    return (
        <div className="ac">
            <header className="ac-topbar">
                <div className="ac-brand">
                    <HeaderWordmark />
                    <span className="ac-scope">{isSuper ? 'Fusable Admin' : 'Team Admin'}</span>
                    {/* Which dealership's team is being administered. A dealer admin
                        can otherwise only infer it from the rows in the table. */}
                    {me?.org_name && <span className="ac-org" title="Organization currently in view">{me.org_name}</span>}
                </div>
                <div className="ac-topbar-spacer" />
                <div className="ac-topbar-right">
                    <button className="ac-switch" onClick={onSwitchToApp} title="Switch to the IronSight dashboard">
                        IronSight view <span aria-hidden="true">→</span>
                    </button>
                    <span className="ac-user">{me?.display_name || 'Admin'}</span>
                    {onLogout && <button className="ac-logout" onClick={onLogout}>Sign out</button>}
                </div>
            </header>

            <div className="ac-scroll">
                <div className="ac-page">
                    <nav className="ac-crumbs" aria-label="Breadcrumb">
                        {view === 'sources' ? (
                            <>
                                <button className="ac-crumb" onClick={() => setView('orgs')}>{crumbs[0].name}</button>
                                <span className="ac-crumb-sep">›</span>
                                <button className="ac-crumb current">Data sources</button>
                            </>
                        ) : crumbs.map((c, i) => (
                            <React.Fragment key={c.id || 'root'}>
                                {i > 0 && <span className="ac-crumb-sep">›</span>}
                                <button
                                    className={`ac-crumb ${i === crumbs.length - 1 ? 'current' : ''}`}
                                    onClick={() => i < crumbs.length - 1 && goTo(c.id)}
                                >
                                    {c.name}
                                </button>
                            </React.Fragment>
                        ))}
                    </nav>

                    {view === 'sources' && <TenantOverview orgs={orgs} canEdit={isSuper} />}

                    {view === 'orgs' && (
                      <>
                    <div className="ac-page-head">
                        <div>
                            <span className="ac-eyebrow">{currentNode ? TYPE_LABEL[currentNode.type] : 'Platform'}</span>
                            <h1 className="ac-title">{currentNode ? currentNode.name : (isSuper ? 'Organizations' : 'Team')}</h1>
                            <p className="ac-subtitle">
                                {currentNode
                                    ? 'Drill into sub-organizations, or manage this level’s team below.'
                                    : (isSuper ? 'Every dealership and holding — drill in to manage teams.' : 'Your dealership team · roles · and IronSight access.')}
                            </p>
                        </div>
                        <div className="ac-head-actions">
                            {isSuper && !currentNode && (
                                <>
                                    <button className="ac-btn-ghost" onClick={() => setView('sources')}>Data sources</button>
                                    <button className="ac-btn-ghost" onClick={() => setShowOnboard(true)}>+ Onboard dealership</button>
                                </>
                            )}
                            {childAdd && (
                                <button className="ac-btn-ghost" onClick={() => openAddChild()}>{childAdd.label}</button>
                            )}
                            {canEdit && <button className="ac-add" onClick={() => setShowAdd(true)}>+ Add user</button>}
                        </div>
                    </div>

                    {(error || actionError) && <div className="ac-error">{actionError || error}</div>}

                    {!loading && (
                        <div className="ac-kpis">
                            {kpis.map((k) => (
                                <div className="ac-kpi" key={k.label}>
                                    <div className="ac-kpi-label"><span className="ac-kpi-dot" />{k.label}</div>
                                    <div className="ac-kpi-value">{k.value}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {childOrgs.length > 0 && (
                        <section className="ac-section">
                            <div className="ac-section-head">
                                <span className="ac-section-title">{childLabel}</span>
                                <span className="ac-section-count">{childOrgs.length}</span>
                            </div>
                            <div className="ac-tiles">
                                {childOrgs.map((node) => (
                                    <div
                                        key={node.id}
                                        className={`ac-tile ${node.status === 'suspended' ? 'suspended' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => drill(node)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') drill(node); }}
                                    >
                                        <div className="ac-tile-top">
                                            <span className={`ac-badge type-${node.type.toLowerCase()}`}>{TYPE_LABEL[node.type]}</span>
                                            {canEdit && (
                                                <div className="ac-tile-acts">
                                                    <button className="ac-tile-act" onClick={(e) => { e.stopPropagation(); setEditTarget(node); }}>Edit</button>
                                                    {isSuper && <button className="ac-tile-act danger" onClick={(e) => { e.stopPropagation(); openDelete(node); }}>Delete</button>}
                                                </div>
                                            )}
                                        </div>
                                        <div className="ac-tile-name">{node.name}</div>
                                        <div className="ac-tile-slug">/{node.slug}</div>
                                        <div className="ac-tile-foot">
                                            <span>{node.status === 'suspended' ? 'Suspended' : tileFoot(node)}</span>
                                            <span className="ac-tile-arrow" aria-hidden="true">→</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    <section className="ac-section">
                        <div className="ac-section-head">
                            <span className="ac-section-title">{teamLabel}</span>
                            <input
                                className="ac-search"
                                type="search"
                                placeholder="Search name or email…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                        <div className="ac-card">
                            <div className="ac-table-wrap">
                                <table className="ac-table">
                                    <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Organization</th>
                                            <th>Role</th>
                                            <th className="ac-app-col">IronSight</th>
                                            <th>Status</th>
                                            <th className="ac-actions-col">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map((u) => {
                                            const isSelf = me?.email && u.email === me.email;
                                            const busy = busyId === u.id;
                                            return (
                                                <tr key={u.id} className={u.status !== 'active' ? 'ac-row-suspended' : ''}>
                                                    <td>
                                                        <span className="ac-name-cell">
                                                            <span className="ac-avatar">{initials(u)}</span>
                                                            <span>
                                                                <span className="ac-name" style={{ display: 'block' }}>{u.display_name}</span>
                                                                <span className="ac-email" style={{ fontSize: '0.78rem' }}>{u.email}</span>
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {canEdit && u.org_id ? (
                                                            <select className="ac-role-select" value={u.org_id} disabled={busy}
                                                                title="Move this user — this also sets what data they see"
                                                                onChange={(e) => patchUser(u, { org_id: e.target.value })}>
                                                                {orgOptions.map((o) => (
                                                                    <option key={o.id} value={o.id}>{`${'  '.repeat(o.depth)}${o.name}`}</option>
                                                                ))}
                                                            </select>
                                                        ) : (u.org_name || '—')}
                                                        {u.org_id && (
                                                            <div className="ac-perspective">{PERSPECTIVE_LABEL[findNode(orgs, u.org_id)?.type] || ''}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {canEdit ? (
                                                            <select className="ac-role-select" value={u.role || 'user'} disabled={busy || isSelf}
                                                                onChange={(e) => patchUser(u, { role: e.target.value })}>
                                                                {roleChoices.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                                                            </select>
                                                        ) : (
                                                            <span className={`ac-role ac-role-${u.role}`}>{ROLE_LABEL[u.role] || u.role}</span>
                                                        )}
                                                    </td>
                                                    <td className="ac-app-col">
                                                        <button className={`ac-app-toggle ${u.apps?.pricepath ? 'on' : 'off'}`} disabled={busy || !canEdit}
                                                            title={u.apps?.pricepath ? 'Revoke IronSight access' : 'Grant IronSight access'}
                                                            onClick={() => patchUser(u, { apps: u.apps?.pricepath ? [] : ['pricepath'] })}>
                                                            {u.apps?.pricepath ? '✓' : '—'}
                                                        </button>
                                                    </td>
                                                    <td><span className={`ac-status ac-status-${u.status}`}>{u.status}</span></td>
                                                    <td className="ac-actions">
                                                        {!canEdit ? <span className="ac-muted">—</span>
                                                            : confirmId === u.id ? (
                                                                <span className="ac-confirm">Remove?
                                                                    <button className="ac-confirm-yes" disabled={busy} onClick={() => doRemove(u)}>Yes</button>
                                                                    <button className="ac-confirm-no" onClick={() => setConfirmId(null)}>No</button>
                                                                </span>
                                                            ) : resetConfirm === u.id ? (
                                                                <span className="ac-confirm">Reset password?
                                                                    <button className="ac-confirm-yes" disabled={busy} onClick={() => doResetPw(u)}>Reset</button>
                                                                    <button className="ac-confirm-no" onClick={() => setResetConfirm(null)}>No</button>
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <button className="ac-act" disabled={busy || isSelf}
                                                                        onClick={() => patchUser(u, { status: u.status === 'active' ? 'suspended' : 'active' })}>
                                                                        {u.status === 'active' ? 'Deactivate' : 'Reactivate'}
                                                                    </button>
                                                                    <button className="ac-act" disabled={busy} onClick={() => setResetConfirm(u.id)}>Reset PW</button>
                                                                    <button className="ac-act ac-act-danger" disabled={isSelf} onClick={() => setConfirmId(u.id)}>Remove</button>
                                                                </>
                                                            )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {!usersLoading && filteredUsers.length === 0 && (
                                            <tr><td colSpan={6}>
                                                <div className="ac-empty-state">
                                                    <svg className="ac-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                                                    </svg>
                                                    {query ? 'No users match your search.' : 'No users at this level yet.'}
                                                </div>
                                            </td></tr>
                                        )}
                                        {usersLoading && (
                                            <tr><td colSpan={6}><div className="ac-empty-state">Loading team…</div></td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <p className="ac-footnote">
                            {COPY.footnoteUsers}
                        </p>
                    </section>
                      </>
                    )}
                </div>
            </div>

            {showAdd && (
                <AddUserModal orgs={orgs} me={me} onClose={() => setShowAdd(false)}
                    onAdded={onUserAdded} />
            )}
            {showOnboard && (
                <OnboardWizard orgs={orgs} onClose={() => setShowOnboard(false)}
                    onDone={onOnboarded} />
            )}

            {editTarget && (
                <OrgEditModal
                    org={editTarget}
                    orgs={orgs}
                    isSuper={isSuper}
                    onClose={() => setEditTarget(null)}
                    onSaved={onOrgSaved}
                />
            )}

            {addChild && (
                <div className="ac-modal-backdrop" onClick={() => setAddChild(null)}>
                    <div className="ac-modal ac-reset-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ac-modal-head">
                            <h2>Add dealership</h2>
                            <button className="ac-modal-x" onClick={() => setAddChild(null)}>×</button>
                        </div>
                        <p className="ac-modal-hint">Adds under <strong>{currentNode?.name}</strong>.</p>
                        <label className="ac-field">
                            <span>Dealership name</span>
                            <input value={addChildName} autoFocus
                                placeholder="e.g. Northern Equipment"
                                onChange={(e) => setAddChildName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') doAddChild(); }} />
                        </label>
                        {orgError && <div className="ac-error">{orgError}</div>}
                        <div className="ac-modal-actions">
                            <button className="ac-btn-ghost" onClick={() => setAddChild(null)} disabled={orgBusy}>Cancel</button>
                            <button className="ac-btn-primary" onClick={doAddChild} disabled={orgBusy || !addChildName.trim()}>{orgBusy ? 'Adding…' : 'Add'}</button>
                        </div>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <div className="ac-modal-backdrop" onClick={() => setDeleteTarget(null)}>
                    <div className="ac-modal ac-reset-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ac-modal-icon" style={{ color: 'var(--pp-danger)', background: 'var(--pp-danger-soft)' }}>
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            </svg>
                        </div>
                        <h3 className="ac-modal-title">Delete “{deleteTarget.name}”?</h3>
                        <p className="ac-modal-text">
                            {COPY.deleteBody(TYPE_LABEL[deleteTarget.type].toLowerCase())}
                        </p>
                        {orgError && <div className="ac-error">{orgError}</div>}
                        <div className="ac-modal-actions">
                            <button className="ac-btn-ghost" onClick={() => setDeleteTarget(null)} disabled={orgBusy}>Cancel</button>
                            <button className="ac-btn-primary" style={{ background: 'var(--pp-danger)' }} onClick={deleteGuard(doDelete)} disabled={orgBusy}>{orgBusy ? 'Deleting…' : 'Delete'}</button>
                        </div>
                    </div>
                </div>
            )}

            {resetResult && (
                <div className="ac-modal-backdrop" onClick={() => setResetResult(null)}>
                    <div className="ac-modal ac-reset-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ac-modal-icon">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="8" cy="15" r="4" /><path d="M10.85 12.15 19 4M18 5l2 2M15 8l2 2" />
                            </svg>
                        </div>
                        <h3 className="ac-modal-title">{resetResult.email_sent ? 'Emailed to the user' : 'Email could not be sent'}</h3>
                        <p className="ac-modal-text">
                            {resetResult.email_sent
                                ? <>We emailed the sign-in details to <strong>{resetResult.email}</strong>. They set their own password on first sign-in.</>
                                : <>The account for <strong>{resetResult.email}</strong> exists, but the sign-in details could not be delivered — so they cannot sign in yet. Check the email service, then use <strong>Reset password</strong> to send them again.</>}
                        </p>
                        <div className="ac-modal-actions">
                            <button className="ac-btn-primary" onClick={() => setResetResult(null)}>Done</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

AdminConsoleV1.propTypes = {
    me: PropTypes.object,
    onSwitchToApp: PropTypes.func.isRequired,
    onLogout: PropTypes.func,
};

// With the new design on, the redesigned console renders inside the v2 app
// shell: the v2 top bar (AppShell/AppTopBar) carries the scope and org chip,
// Sign out, and leaving the console through the dealership picker (D18).
function AdminConsole(props) {
    const uiV2 = useUiV2();
    // eslint-disable-next-line react/destructuring-assignment
    return uiV2 ? <AdminConsoleV2 me={props.me} /> : <AdminConsoleV1 {...props} />;
}

AdminConsole.propTypes = AdminConsoleV1.propTypes;

export default AdminConsole;
